// Web-specific Social Auth Service
// Uses Google Identity Services (GIS) for more reliable web OAuth

// Google OAuth configuration
const GOOGLE_CLIENT_ID_WEB = process.env.REACT_APP_GOOGLE_CLIENT_ID || 
  process.env.EXPO_PUBLIC_GOOGLE_CLIENT_ID ||
  '918164682944-qq5blacapi2cb6q0tch01vbf4n6gut4n.apps.googleusercontent.com';

// Track if GIS is loaded
let gisLoaded = false;
let gisLoadPromise = null;

class SocialAuthService {
  /**
   * Check if Apple Sign-In is available (not available on web)
   */
  static async isAppleSignInAvailable() {
    return false;
  }

  /**
   * Sign in with Apple (not available on web)
   */
  static async signInWithApple() {
    return { 
      success: false, 
      error: 'Apple Sign-In is only available on iOS devices' 
    };
  }

  /**
   * Load Google Identity Services script
   */
  static loadGoogleScript() {
    if (gisLoaded) {
      return Promise.resolve();
    }
    
    if (gisLoadPromise) {
      return gisLoadPromise;
    }

    gisLoadPromise = new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.google?.accounts) {
        gisLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        gisLoaded = true;
        console.log('✅ Google Identity Services loaded');
        resolve();
      };
      script.onerror = () => {
        gisLoadPromise = null;
        reject(new Error('Failed to load Google Identity Services'));
      };
      document.head.appendChild(script);
    });

    return gisLoadPromise;
  }

  /**
   * Sign in with Google using Google Identity Services (GIS)
   */
  static async signInWithGoogle() {
    try {
      const clientId = GOOGLE_CLIENT_ID_WEB;
      
      // Check if we have a valid client ID
      if (!clientId || clientId.includes('YOUR_GOOGLE_WEB_CLIENT_ID') || clientId.length < 20) {
        const errorMsg = 'Google Sign-In is not configured. Please set up Google OAuth credentials.';
        console.error('❌', errorMsg);
        return { success: false, error: errorMsg, cancelled: false };
      }

      console.log('✅ Google Client ID configured');
      console.log('🔄 Loading Google Identity Services...');

      // Load GIS script
      await SocialAuthService.loadGoogleScript();
      
      console.log('🔄 Initiating Google Sign-In...');

      return new Promise((resolve) => {
        // Use Google's token client for OAuth 2.0 implicit flow
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: 'openid email profile',
          callback: async (tokenResponse) => {
            if (tokenResponse.error) {
              console.log('❌ Google auth error:', tokenResponse.error);
              resolve({ 
                success: false, 
                cancelled: tokenResponse.error === 'access_denied',
                error: tokenResponse.error_description || tokenResponse.error 
              });
              return;
            }

            if (tokenResponse.access_token) {
              console.log('✅ Got access token from Google');
              try {
                const userInfo = await SocialAuthService.fetchUserInfo(tokenResponse.access_token);
                console.log('✅ Fetched user info:', userInfo.email);
                resolve({
                  success: true,
                  provider: 'google',
                  accessToken: tokenResponse.access_token,
                  email: userInfo.email,
                  name: userInfo.name,
                  givenName: userInfo.given_name,
                  familyName: userInfo.family_name,
                  picture: userInfo.picture,
                  id: userInfo.id,
                });
              } catch (err) {
                console.error('❌ Error fetching user info:', err);
                resolve({ success: false, error: err.message });
              }
            } else {
              console.log('❌ No access token received');
              resolve({ success: false, error: 'No access token received' });
            }
          },
          error_callback: (error) => {
            console.log('❌ Google auth error callback:', error);
            resolve({ 
              success: false, 
              cancelled: error.type === 'popup_closed',
              error: error.message || 'Authentication failed' 
            });
          }
        });

        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '81b6b1' },
          body: JSON.stringify({
            sessionId: '81b6b1',
            runId: 'reroute-investigation-1',
            hypothesisId: 'H3',
            location: 'SocialAuthService.web.js:requestAccessToken',
            message: 'Requesting Google access token with prompt',
            data: { prompt: 'select_account' },
            timestamp: Date.now(),
          }),
        }).catch(() => {});
        // #endregion agent log
        // Request the token (opens popup)
        tokenClient.requestAccessToken({ prompt: 'select_account' });
      });
    } catch (error) {
      console.error('❌ Google Web Sign-In error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if there's a pending Google auth result (for redirect flow - not used with GIS)
   */
  static checkPendingGoogleAuth() {
    return { hasPending: false };
  }

  /**
   * Helper function to fetch user info from Google
   */
  static async fetchUserInfo(accessToken) {
    const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch user info from Google');
    }
    
    return await response.json();
  }

  /**
   * Get a display name from social auth result
   */
  static getDisplayName(authResult) {
    if (authResult.provider === 'apple') {
      if (authResult.fullName) {
        const { givenName, familyName } = authResult.fullName;
        if (givenName || familyName) {
          return `${givenName || ''} ${familyName || ''}`.trim();
        }
      }
      return authResult.email?.split('@')[0] || 'Apple User';
    }
    
    if (authResult.provider === 'google') {
      return authResult.name || authResult.email?.split('@')[0] || 'Google User';
    }
    
    return 'User';
  }

  /**
   * Generate a username from email
   */
  static generateUsername(email) {
    if (!email) return `user_${Date.now()}`;
    
    let username = email.split('@')[0]
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .substring(0, 20);
    
    const suffix = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `${username}_${suffix}`;
  }
}

export default SocialAuthService;
